import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { keepReturnTo } from '../components/common/returnPath';
import api from '../services/api';
import { useProject } from '../contexts/ProjectContext';
import LoadingSpinner from '../components/LoadingSpinner';
import SettingsLayout, {
  SettingsSection,
} from '../components/settings/SettingsLayout';
import KnowledgeBriefPanel from '../components/KnowledgeBriefPanel';
import KnowledgeSuggestLauncher from '../components/KnowledgeSuggestLauncher';
import DocumentsPanel from '../components/DocumentsPanel';
import ImagesPanel from '../components/ImagesPanel';
import type { Category } from '../types';

type Section = 'general' | 'knowledge' | 'documents' | 'images' | 'danger';

const SECTIONS: SettingsSection[] = [
  { key: 'general', label: 'General' },
  { key: 'knowledge', label: 'Knowledge' },
  { key: 'documents', label: 'Documents' },
  { key: 'images', label: 'Images' },
  { key: 'danger', label: 'Danger zone', danger: true },
];

export default function CategorySettings() {
  const { categoryId: idParam, section: sectionParam } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshCategories } = useProject();
  const categoryId = Number(idParam);
  const section = (sectionParam || 'general') as Section;

  const [category, setCategory] = useState<Category | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategory = useCallback(async () => {
    try {
      const c = await api.getCategory(categoryId);
      setCategory(c);
      if (c.project_id) {
        try {
          const p = await api.getProject(c.project_id);
          setProjectName(p.name);
        } catch { /* best-effort */ }
      }
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load category');
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  // The section rail must not eat the way out. `section` is a route segment, so
  // navigating to it drops the query string — and with it the `?from=` that the
  // header's back button is pointing at. Looking around the sections before
  // heading back is the normal case, not a reason to strand someone.
  const handleSectionChange = (key: string) => {
    const qs = keepReturnTo(searchParams, new URLSearchParams()).toString();
    navigate(`/categories/${categoryId}/settings/${key}` + (qs ? `?${qs}` : ''));
  };

  if (loading) {
    return (
      <div className="p-6">
        <LoadingSpinner />
      </div>
    );
  }
  if (error || !category) {
    return (
      <div className="p-6 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded">
        {error ?? 'Category not found'}
      </div>
    );
  }

  // The `category-` spelling is right here: `/categories/:id` is served by the
  // legacy shim, which reports `legacy_category_id or topic.id`, so this id is a
  // LEGACY category id whenever the node predates migration 108.
  // `parseNodeParam` resolves that prefix by legacy id, and falls back to
  // reading it as a node id for roots created since — which is what a category
  // made after the migration has.
  const backToCategory = `/subjects/${category.project_id}?node=category-${categoryId}`;

  return (
    <SettingsLayout
      title={category.name}
      scopeLabel="Category"
      breadcrumb={[
        { label: 'Subjects', to: '/subjects' },
        {
          label: projectName ?? 'Subject',
          to: `/subjects/${category.project_id}`,
        },
        { label: category.name, to: backToCategory },
        { label: 'Settings' },
      ]}
      backTo={backToCategory}
      backLabel="Back to category"
      sections={SECTIONS}
      activeSection={section}
      onSectionChange={handleSectionChange}
    >
      {section === 'general' && (
        <GeneralSection
          category={category}
          onSaved={() => {
            fetchCategory();
            refreshCategories();
          }}
        />
      )}
      {section === 'knowledge' && (
        <>
          <KnowledgeSuggestLauncher
            scope="category"
            scopeId={categoryId}
            scopeName={category.name}
          />
          <KnowledgeBriefPanel
            scope="category"
            scopeId={categoryId}
            scopeName={category.name}
          />
        </>
      )}
      {section === 'documents' && (
        <DocumentsPanel category_id={categoryId} label="Category Documents" />
      )}
      {section === 'images' && (
        <ImagesPanel category_id={categoryId} label="Category Images" />
      )}
      {section === 'danger' && (
        <DangerSection
          category={category}
          onDeleted={() => {
            refreshCategories();
            navigate(`/subjects/${category.project_id}`);
          }}
        />
      )}
    </SettingsLayout>
  );
}

// -----------------------------------------------------------------------------
// General — name, slug, description, active toggle
// -----------------------------------------------------------------------------

function GeneralSection({
  category,
  onSaved,
}: {
  category: Category;
  onSaved: () => void;
}) {
  const [name, setName] = useState(category.name);
  const [slug, setSlug] = useState(category.slug ?? '');
  const [description, setDescription] = useState(category.description ?? '');
  const [isActive, setIsActive] = useState(category.is_active);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    name !== category.name ||
    slug !== (category.slug ?? '') ||
    description !== (category.description ?? '') ||
    isActive !== category.is_active;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.updateCategory(category.id, {
        name,
        slug: slug || null,
        description: description || null,
        is_active: isActive,
      });
      onSaved();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-5 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">General</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Name, slug, description, and status.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Slug</label>
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="auto-derived from name if blank"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm"
        />
      </div>

      <div>
        <button
          type="button"
          onClick={() => setIsActive(!isActive)}
          className={`px-3 py-1.5 text-sm rounded ${
            isActive
              ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          {isActive ? 'Active' : 'Inactive'}
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700 disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function DangerSection({
  category,
  onDeleted,
}: {
  category: Category;
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteCategory(category.id);
      onDeleted();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to delete');
      setBusy(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded p-5 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-red-700 dark:text-red-300">Danger zone</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Deleting this category removes all of its topics, keywords, pages, and
          knowledge briefs. This cannot be undone.
        </p>
      </div>
      {error && (
        <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
          {error}
        </div>
      )}
      {confirming ? (
        <div className="flex items-center gap-2">
          <button
            onClick={handleDelete}
            disabled={busy}
            className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? 'Deleting…' : `Yes, delete "${category.name}"`}
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={busy}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm rounded hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setConfirming(true)}
          className="px-3 py-1.5 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-sm rounded hover:bg-red-50 dark:hover:bg-red-900/30"
        >
          Delete category
        </button>
      )}
    </div>
  );
}
